// Home lobby: 3D background + central character + team slots + mini profile.
import { useEffect, useState } from "react";
import { useGameStore, SCREEN } from "@/stores/gameStore";
import { useSettings } from "@/stores/settingsStore";
import { useT } from "@/i18n";
import { useUiSounds } from "@/lib/audio";
import { fetchProfile } from "@/lib/api";
import { agentForCode } from "@/game/agents/agents";
import LobbyLayout from "@/components/LobbyLayout";
import LobbyCharacter from "@/components/LobbyCharacter";
import AgentSelector from "@/components/AgentSelector";
import PartyInviteDialog from "@/components/PartyInviteDialog";
import { UserPlus, Clock } from "lucide-react";
import { MENU } from "@/constants/testIds";

export default function HomeScreen() {
    const t = useT();
    const goto = useGameStore((s) => s.goto);
    const sfx = useUiSounds();
    const [profile, setProfile] = useState(null);
    const [partyDialog, setPartyDialog] = useState(null); // slot index
    const partySlots = useSettings((s) => s.partySlots) || [null, null];

    useEffect(() => {
        fetchProfile().then(setProfile).catch(() => {});
    }, []);

    // Mock accept simulation — after 3s any "pending" invite flips to
    // "accepted" so we can preview the teammate. The real multiplayer
    // layer will replace this hook with a websocket event.
    const update = useSettings((s) => s.update);
    useEffect(() => {
        const t = setTimeout(() => {
            const next = partySlots.map((slot) => {
                if (slot && slot.status === "pending" && Date.now() - slot.sentAt > 3000) {
                    return { ...slot, status: "accepted" };
                }
                return slot;
            });
            if (JSON.stringify(next) !== JSON.stringify(partySlots)) {
                update({ partySlots: next });
            }
        }, 3200);
        return () => clearTimeout(t);
    }, [partySlots, update]);

    const openSlot = (i) => {
        sfx.open();
        setPartyDialog(i);
    };

    return (
        <LobbyLayout active={SCREEN.MENU}>
            <div data-testid={MENU.root} className="relative w-full h-full">
                {/* Team slots */}
                <div className="absolute inset-0 flex items-end justify-between px-[6vw] pb-[6vh] pointer-events-none">
                    <TeamSlot
                        testId="team-slot-left"
                        label={t("lobby.slot_left")}
                        invite={partySlots[0]}
                        onOpen={() => openSlot(0)}
                        onHover={sfx.hover}
                    />
                    <div className="w-[560px] max-w-[42vw]" />
                    <TeamSlot
                        testId="team-slot-right"
                        label={t("lobby.slot_right")}
                        invite={partySlots[1]}
                        onOpen={() => openSlot(1)}
                        onHover={sfx.hover}
                    />
                </div>

                {/* Character */}
                <LobbyCharacter />

                {/* Agent selector — floating top-left of the character */}
                <div
                    className="absolute left-[6vw] top-[14vh] z-[3] pointer-events-auto"
                    style={{ maxWidth: 240 }}
                >
                    <AgentSelector />
                </div>

                {/* Status line above the character */}
                <div className="absolute top-[10vh] left-1/2 -translate-x-1/2 z-[3] text-center pointer-events-none">
                    <div className="axis-mono text-[10px] tracking-[0.65em] text-white/50">
                        {t("lobby.status")}
                    </div>
                </div>

                {/* Mini profile card */}
                {profile && (
                    <button
                        data-testid="mini-profile-card"
                        onClick={() => { sfx.click(); goto(SCREEN.PROFILE); }}
                        onMouseEnter={sfx.hover}
                        className="pointer-events-auto absolute left-1/2 -translate-x-1/2 bottom-[15vh] z-[3] axis-glass axis-cut-sm px-4 py-2.5 flex items-center gap-3 border border-white/15 hover:border-white/40 transition min-w-[300px]"
                    >
                        <div
                            className="w-9 h-9 axis-cut-sm border flex items-center justify-center"
                            style={{
                                borderColor: profile.avatar_color,
                                background: `${profile.avatar_color}22`,
                                color: profile.avatar_color,
                            }}
                        >
                            <span className="axis-heading text-sm">
                                {profile.name.charAt(0).toUpperCase()}
                            </span>
                        </div>
                        <div className="flex flex-col text-left leading-tight flex-1">
                            <div className="flex items-center gap-2">
                                <span className="axis-heading text-sm text-white tracking-wider">
                                    {profile.name}
                                </span>
                                <span className="axis-mono text-[9px] tracking-[0.3em] px-1.5 py-0.5 border border-orange-400/60 text-orange-300 axis-cut-sm">
                                    {t("common.beta")}
                                </span>
                            </div>
                            <div className="flex items-center gap-2 axis-mono text-[10px] tracking-[0.3em] text-white/50">
                                <span>{t("profile.level")} {profile.level}</span>
                                <span className="text-white/25">·</span>
                                <span>{profile.xp} {t("profile.xp")}</span>
                                <span className="text-white/25">·</span>
                                <span className="text-cyan-300/80">{t("lobby.mini_status")}</span>
                            </div>
                            <div className="mt-1 h-0.5 bg-white/10 axis-cut-sm overflow-hidden">
                                <div
                                    className="h-full bg-orange-400"
                                    style={{ width: `${(profile.xp % 500) / 5}%` }}
                                />
                            </div>
                        </div>
                    </button>
                )}
            </div>

            <PartyInviteDialog
                open={partyDialog !== null}
                slotIndex={partyDialog || 0}
                onClose={() => setPartyDialog(null)}
            />
        </LobbyLayout>
    );
}

function TeamSlot({ testId, label, invite, onOpen, onHover }) {
    const pending = !!invite;
    // Mock "accept" — after 3s a queued invite becomes accepted and we can
    // preview the teammate's operator. Deterministic per friend code so
    // the same code always shows the same agent.
    const accepted = pending && invite.status === "accepted";
    const previewAgent = pending ? agentForCode(invite.code) : null;
    return (
        <button
            data-testid={testId}
            onClick={onOpen}
            onMouseEnter={onHover}
            className={`pointer-events-auto axis-cut-sm border transition flex flex-col items-center justify-center w-36 h-48 group relative ${
                accepted
                    ? "border-cyan-400 bg-cyan-500/10"
                    : pending
                        ? "border-orange-400 bg-orange-500/10"
                        : "border-dashed border-white/20 hover:border-white/50 bg-black/40 hover:bg-black/60"
            }`}
        >
            {pending ? (
                <>
                    {/* Agent silhouette preview */}
                    <div
                        className="w-14 h-20 axis-cut-sm flex items-end justify-center overflow-hidden"
                        style={{
                            background: `linear-gradient(180deg, ${previewAgent.palette.shirt} 0%, ${previewAgent.palette.trousers} 60%, ${previewAgent.palette.boots} 100%)`,
                            border: `1px solid ${previewAgent.palette.beltAccent}`,
                        }}
                    >
                        {/* stylised head + vest silhouette */}
                        <svg viewBox="0 0 40 60" width="100%" height="100%">
                            <circle cx="20" cy="12" r="6" fill={previewAgent.palette.skin} />
                            <rect x="10" y="18" width="20" height="20" fill={previewAgent.palette.vest} />
                            <rect x="10" y="36" width="20" height="4" fill={previewAgent.palette.beltAccent} />
                            <rect x="12" y="40" width="16" height="20" fill={previewAgent.palette.trousers} />
                        </svg>
                    </div>
                    <div className="mt-2 axis-heading text-[11px] tracking-widest text-white">
                        {previewAgent.name}
                    </div>
                    <div className="axis-mono text-[9px] tracking-[0.3em] text-white/50">
                        {invite.code}
                    </div>
                    <div
                        className={`axis-mono text-[9px] tracking-[0.3em] mt-1 ${
                            accepted ? "text-cyan-300" : "text-orange-300/80"
                        }`}
                    >
                        {accepted ? "READY" : "PENDING"}
                    </div>
                </>
            ) : (
                <>
                    <UserPlus size={28} className="text-white/40 group-hover:text-white/70 transition" />
                    <div className="mt-2 axis-mono text-[9px] tracking-[0.3em] text-white/40">
                        {label.toUpperCase()}
                    </div>
                </>
            )}
        </button>
    );
}
